import "../../kendo.icons.js";
import "../../kendo.dropdownbutton.js";
import "../../kendo.button.js";
import { Widget } from "../../core/base/widget";
import type { DomUtilsService } from "../../core/services/dom-utils.service";
import type { HtmlService } from "../../core/services/html.service";
import type { DateParserService } from "../../core/services/date-parser.service";
import { DragToScrollHandler } from "../../core/models/dom-utils";
import { NS, STYLES, REFERENCES, ICONS, SCROLLING_DELTA, CLICK, DOT, RTL, EVENTS } from "../constants";
import type { IChatOptions, IMessage } from "../models";
import type { DataManager } from "./data-manager.collaborator.js";
import { MessageToolbar } from "./message-toolbar.collaborator.js";
import { FileMenu } from "./file-menu.collaborator.js";
import { IKendoJQueryStatic, IKendoJQuery } from "../../core";
import { MessageRenderer, ScrollManager, SuggestionRenderer } from "./chat-view";

declare const kendo: any;
declare const $: IKendoJQueryStatic;

export interface IChatViewContext {
    dataManager: DataManager;
    domUtilsService: DomUtilsService;
    htmlService: HtmlService;
    dateParserService: DateParserService;
}

export class ChatView extends Widget {
    static options = {
        name: "ChatView"
    };

    private context: IChatViewContext;
    private list!: IKendoJQuery;
    private _dragHandler: DragToScrollHandler | null = null;
    private _messageRenderer!: MessageRenderer;
    private _scrollManager!: ScrollManager;
    private _suggestionRenderer!: SuggestionRenderer;

    constructor(
        element: IKendoJQuery,
        options: Partial<IChatOptions>,
        context: IChatViewContext
    ) {
        super(element, $.extend(true, {}, ChatView.options, options));

        this.context = context;
        this._initList();
        this._initHelpers();
        this._attachEvents();
    }

    events: string[] = [
        EVENTS.suggestedActionClick,
        EVENTS.messageToolbarExecute,
        EVENTS.fileMenuExecute,
        EVENTS.downloadAllFiles,
        EVENTS.expandableToggle,
        EVENTS.resendMessage
    ];

    private _initList(): void {
        const messages = (this.options as IChatOptions).messages;

        this.element
            .addClass(STYLES.viewWrapper)
            .attr("role", "log")
            .attr("aria-label", messages.messageListLabel);

        this.list = $("<div>")
            .addClass(STYLES.messageListContent)
            .appendTo(this.element);
    }

    private _initHelpers(): void {
        const options = this.options as IChatOptions;

        this._messageRenderer = new MessageRenderer({
            list: this.list,
            options,
            dataManager: this.context.dataManager,
            htmlService: this.context.htmlService,
            dateParserService: this.context.dateParserService,
            dataItem: this.dataItem.bind(this),
            getReplyMessage: this.getReplyMessage.bind(this),
            initMessageToolbar: this._initMessageToolbar.bind(this),
            initFileMenus: this._initFileMenus.bind(this)
        });

        this._scrollManager = new ScrollManager({
            element: this.element,
            options,
            list: this.list,
            domUtilsService: this.context.domUtilsService,
            htmlService: this.context.htmlService
        });

        this._suggestionRenderer = new SuggestionRenderer({
            list: this.list,
            options,
            dataManager: this.context.dataManager
        });
    }

    private _attachEvents(): void {
        this.element
            .on(CLICK + NS, this._listClick.bind(this))
            .on(CLICK + NS, DOT + STYLES.message, this._messageClick.bind(this))
            .on(CLICK + NS, DOT + STYLES.suggestion, this._suggestionClick.bind(this))
            .on(CLICK + NS, `[${REFERENCES.leftScrollButton}]`, this._leftScrollButtonClick.bind(this))
            .on(CLICK + NS, `[${REFERENCES.rightScrollButton}]`, this._rightScrollButtonClick.bind(this))
            .on(CLICK + NS, DOT + STYLES.downloadButton, this._downloadAllClick.bind(this))
            .on(CLICK + NS, `[${REFERENCES.retryButton}]`, this._retryButtonClick.bind(this));

        this._attachDragToScrollEvents();
    }

    private _attachDragToScrollEvents(): void {
        this._dragHandler = this.context.domUtilsService.createDragToScrollHandler(
            this.element,
            {
                namespace: NS + ".chatViewDrag",
                captureElement: this.element,
                delegateSelector: DOT + STYLES.suggestionsScroll
            }
        );
        this._dragHandler.attach();
    }

    private _retryButtonClick(e: any): void {
        e.preventDefault();
        e.stopPropagation();
        const button = $(e.currentTarget);
        const uid = button.attr("data-uid");
        const messageElement = button.closest(DOT + STYLES.message) as IKendoJQuery;
        const message = this._getMessageFromElement(messageElement);

        this.trigger(EVENTS.resendMessage, { message, uid });
    }

    private _getMessageFromElement(element: IKendoJQuery): IMessage | null {
        const uid = element.attr("data-uid");
        if (!uid) {
            return null;
        }
        const options = this.options as IChatOptions;
        const messages = options.dataSource?.data() || [];
        return messages.find((m: any) => m.uid === uid) || { uid };
    }

    renderMessage(message: IMessage): void {
        this._messageRenderer.renderMessage(message);
    }

    _initMessageToolbar(element: IKendoJQuery): void {
        const options = this.options as IChatOptions;
        const messageToolbarActions = options.messageToolbarActions;

        if (!messageToolbarActions || !messageToolbarActions.length || !element.length) {
            element.remove();
            return;
        }

        const messageToolbar = new MessageToolbar(element, {
            items: messageToolbarActions,
            dir: options.dir,
            messages: options.messages,
            resizable: false
        });

        messageToolbar.bind("execute", (e: any) => this.trigger(EVENTS.messageToolbarExecute, e));
    }

    _initFileMenus(element: IKendoJQuery): void {
        const options = this.options as IChatOptions;
        const fileActions = options.fileActions;
        const fileMenuButtons = element.find(`[${REFERENCES.fileMenuButton}]`);

        if (!fileActions || !fileActions.length || !element.length) {
            fileMenuButtons.remove();
            return;
        }

        fileMenuButtons.each((_i: number, el: HTMLElement) => {
            const $el = $(el);
            if (!$el.data("kendoDropDownButton")) {
                const menu = new FileMenu($el, { items: fileActions });
                menu.bind("execute", (e: any) => this.trigger(EVENTS.fileMenuExecute, e));
            }
        });
    }

    dataItem(message: IKendoJQuery): IMessage | null {
        const uid = message && message.data("uid");
        if (uid) {
            return this.context.dataManager.getMessageByUid(uid);
        }
        return null;
    }

    fileDataItem(message: IMessage, file: IKendoJQuery): any {
        const uid = file && file.data("uid");
        if (uid) {
            return this.context.dataManager.getFileByUid(message, uid);
        }
        return null;
    }

    getReplyMessage(message: IMessage): IMessage | null {
        if (!message?.replyToId) {
            return null;
        }

        const replyMessage = this.context.dataManager.getMessageById(message.replyToId);
        if (replyMessage) {
            (replyMessage as any).isOwnMessage = replyMessage.authorId === (this.options as IChatOptions).authorId;
        }

        return replyMessage;
    }

    renderSuggestedActions(): void {
        this._suggestionRenderer.renderSuggestedActions();
    }

    clearMessages(): void {
        this.list.find(DOT + STYLES.messageGroup).remove();
        this.list.find(DOT + STYLES.timestamp).remove();
        this._suggestionRenderer.removeSuggestedActions();
    }

    removeMessage(uid: string): boolean {
        const messageElement = this.list.find(DOT + STYLES.message + `[data-uid="${this.context.htmlService.encode(uid)}"]`);

        if (!messageElement.length) {
            return false;
        }

        const messageGroup = messageElement.closest(DOT + STYLES.messageGroup);
        const messagesInGroup = messageGroup.find(DOT + STYLES.message);

        if (messagesInGroup.length === 1) {
            messageGroup.remove();
        } else {
            messageElement.remove();
        }

        return true;
    }

    scrollToBottom(): void {
        this._scrollManager.scrollToBottom();
    }

    scrollToMessage(uid: string): boolean {
        return this._scrollManager.scrollToMessage(uid);
    }

    refreshDataManager(dataManager: DataManager): void {
        this.context.dataManager = dataManager;
    }

    showNoData(html: string): void {
        this.list.html(html);
    }

    hasMessages(): boolean {
        return this.list.children(DOT + STYLES.messageGroup).length > 0;
    }

    private _listClick(e: any): void {
        const targetElement = $(e.target);

        if (targetElement.hasClass(STYLES.message) || targetElement.parents(DOT + STYLES.message).length) {
            return;
        }

        this._clearSelection(targetElement);
    }

    private _suggestionClick(e: any): void {
        const suggestionElement = $(e.currentTarget);

        if (suggestionElement.length) {
            this.trigger(EVENTS.suggestedActionClick, { text: suggestionElement.text() });
        }
    }

    private _messageClick(e: any): void {
        const target = $(e.target);
        const expandIcon = target.closest(`[${REFERENCES.bubbleExpandableIndicator}]`);

        if (this._allowMessageClick(target)) {
            return;
        }

        this._clearSelection(target);

        if (expandIcon.length) {
            const bubble = expandIcon.closest(DOT + STYLES.bubble);
            const isExpanded = bubble.hasClass(STYLES.expanded);

            bubble.toggleClass(STYLES.expanded, !isExpanded);
            expandIcon.html(kendo.ui.icon({ icon: isExpanded ? ICONS.chevronDown : ICONS.chevronUp }));

            this.trigger(EVENTS.expandableToggle, { indicator: expandIcon, isExpanded: !isExpanded });
            return;
        }

        const messageElement = $(e.currentTarget);
        const bubble = messageElement.find(DOT + STYLES.bubble);
        bubble.addClass(STYLES.selected);
    }

    private _allowMessageClick(target: IKendoJQuery): boolean {
        const disallowedSelectors = [
            `[${REFERENCES.fileMenuButton}]`,
            DOT + STYLES.messageToolbar,
            DOT + STYLES.typingIndicator,
            DOT + STYLES.downloadButton
        ].join(", ");

        return target.closest(disallowedSelectors).length > 0 ||
            target.children(disallowedSelectors).length > 0 ||
            target.is(disallowedSelectors);
    }

    private _clearSelection(target: IKendoJQuery): void {
        const selectedMessages = this.element.find(DOT + STYLES.selected);

        if (target.closest(`[${REFERENCES.bubbleExpandableIndicator}]`).length) {
            return;
        }

        selectedMessages.each((_index: number, element: HTMLElement) => {
            const bubble = $(element);
            bubble.removeClass(STYLES.selected);
        });
    }

    private _leftScrollButtonClick(e: any): void {
        e.preventDefault();
        const button = $(e.currentTarget);
        const scrollableElement = this.element.find(`${DOT}${STYLES.suggestionsScroll}`);
        const isRtl = (this.options as IChatOptions).dir === RTL;

        const position = this.context.domUtilsService.scrollByDelta(scrollableElement, isRtl ? SCROLLING_DELTA : -SCROLLING_DELTA);

        this.element.find(`[${REFERENCES.rightScrollButton}]`).removeClass(STYLES.disabled);

        if (position.atStart) {
            button.addClass(STYLES.disabled);
        }
    }

    private _rightScrollButtonClick(e: any): void {
        e.preventDefault();
        const button = $(e.currentTarget);
        const scrollableElement = this.element.find(`${DOT}${STYLES.suggestionsScroll}`);
        const isRtl = (this.options as IChatOptions).dir === RTL;

        const position = this.context.domUtilsService.scrollByDelta(scrollableElement, isRtl ? -SCROLLING_DELTA : SCROLLING_DELTA);

        this.element.find(`[${REFERENCES.leftScrollButton}]`).removeClass(STYLES.disabled);

        if (position.atEnd) {
            button.addClass(STYLES.disabled);
        }
    }

    private _downloadAllClick(e: any): void {
        const downloadButton = $(e.currentTarget);
        const message = downloadButton.closest(DOT + STYLES.message);

        if (message.length) {
            this.trigger(EVENTS.downloadAllFiles, { messageElement: message });
        }
    }

    destroy(): void {
        super.destroy();

        this._scrollManager.destroy();

        if (this._dragHandler) {
            this._dragHandler.destroy();
        }

        this.element.find("[data-role='button']").each(function() {
            const fileMenu = $(this).data("kendoChatFileMenu");
            if (fileMenu) {
                fileMenu.destroy();
            }
        });

        this.element.empty();
        this.element.off(NS);

        (this as any).list = null;
    }
}
